// skills/woodcutting/woodcuttingUI.js
// Woodcutting UI - Gatherer-style card layout

import { woodcuttingSkill } from './woodcutting.js';
import { TREES, AXES } from './woodcuttingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatWithCommas } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentLootBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { getAtlasPercentBg } from '../../data/atlasManifest.js';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

/**
 * Get sprite HTML for a tree
 */
function getTreeSprite(tree) {
    if (!tree.sprite) return '🌲';

    const { sheet, x = 0, y = 0, width = 64, height = 64 } = tree.sprite;
    const isLarge = width >= 48 || height >= 48;
    const sizeClass = isLarge ? ' sprite-icon-lg' : '';
    const isSingleImage = x === 0 && y === 0;
    if (isSingleImage) {
        const atlas = getAtlasPercentBg(sheet);
        if (atlas) {
            return `<span class="sprite-icon ${sizeClass}" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition}; width: ${width}px; height: ${height}px;" data-hd-src="${sheet}"></span>`;
        }
    }
    const bgSize = isSingleImage ? 'background-size: contain;' : '';
    return `<span class="sprite-icon${sizeClass}" style="background-image: url('${sheet}'); background-position: -${x}px -${y}px; width: ${width}px; height: ${height}px; ${bgSize}"></span>`;
}

/**
 * Render compact header with axe upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.WOODCUTTING);
    const xpPerHour = getXPPerHour(SKILLS.WOODCUTTING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.WOODCUTTING);

    // Tool info
    const activeTool = woodcuttingSkill.getActiveBuff();
    const axes = Object.values(AXES);
    const currentIndex = axes.findIndex(a => a.id === activeTool.id);
    const nextAxe = axes[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextAxe) {
        const canAfford = playerGold >= nextAxe.price;
        if (skill.level < nextAxe.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextAxe.name} (${nextAxe.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextAxe.price}">${getItemIcon('coins')} ${formatWithCommas(nextAxe.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextAxe.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.woodcuttingUI.upgradeAxe('${nextAxe.id}')">
                    → ${nextAxe.name} (${nextAxe.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextAxe.price}">${getItemIcon('coins')} ${formatWithCommas(nextAxe.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.WOODCUTTING)}</span>
                    <span class="compact-name">Woodcutting</span>
                    ${renderSkillInfo(SKILLS.WOODCUTTING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.WOODCUTTING)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Axe:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render a single tree card
 */
function renderTreeCard(tree, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= tree.level;
    const isActive = currentAction?.type === 'woodcutting' && currentAction?.tree?.id === tree.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = woodcuttingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.WOODCUTTING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.WOODCUTTING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = tree.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.WOODCUTTING);
    let effectiveXP = tree.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities (talent + corruption)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.WOODCUTTING);
    const talentLootBonus = getTalentLootBonus(SKILLS.WOODCUTTING);

    // Build drop display (trees have single drops)
    const drop = tree.drops[0];
    const itemName = getItemName(drop.itemId);
    const itemIcon = getItemIcon(drop.itemId);
    const baseQty = drop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="drop-qty boosted">x${effectiveQty}</span>`
        : `<span class="drop-qty">x${baseQty}</span>`;

    const cardClasses = [
        'tree-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-tree-id="${tree.id}"
             style="--tree-color: ${tree.color}">

            <div class="tree-card-header">
                <span class="tree-card-level">${isUnlocked ? `Lvl ${tree.level}` : `Req: ${tree.level}`}</span>
                <span class="tree-card-icon">${getTreeSprite(tree)}</span>
                <span class="tree-card-name">${tree.name}</span>
            </div>

            <div class="tree-card-info">
                <div class="tree-stat">
                    <span class="stat-icon woodcutting-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="tree-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="tree-drops">
                <div class="drop-info log-drop">
                    <span class="drop-name">${itemIcon} ${itemName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="tree-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.woodcuttingUI.toggleChopping('${tree.id}')">
                        ${isActive ? 'Stop' : 'Chop'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="tree-progress">
                <div class="tree-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render the woodcutting interface
 */
export function renderWoodcuttingUI() {
    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.WOODCUTTING);
    const currentAction = gameState.getCurrentAction();

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.WOODCUTTING);

    const treesHtml = Object.values(TREES)
        .map(tree => renderTreeCard(tree, skill.level, currentAction))
        .join('');

    container.innerHTML = `
        <div class="woodcutting-interface">
            ${renderCompactHeader()}
            <div class="tree-grid">
                ${treesHtml}
            </div>
        </div>
    `;
}

/**
 * Toggle chopping on/off for a tree
 */
export function toggleChopping(treeId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.tree?.id === treeId) {
        woodcuttingSkill.stopChopping();
    } else {
        const result = woodcuttingSkill.canChop(treeId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            woodcuttingSkill.startChopping(treeId);
        }
    }

    renderWoodcuttingUI();
}

/**
 * Upgrade axe
 */
export function upgradeAxe(axeId) {
    const result = woodcuttingSkill.upgradeAxe(axeId);

    if (result.success) {
        renderWoodcuttingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Update woodcutting UI each tick
 */
export function updateWoodcuttingUI() {
    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.WOODCUTTING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderWoodcuttingUI();
        }
        return;
    }

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.WOODCUTTING);
    const xpPerHour = getXPPerHour(SKILLS.WOODCUTTING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.WOODCUTTING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.woodcutting-interface');

    const xpTotalEl = document.querySelector('.woodcutting-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.woodcutting-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.woodcutting-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    updateCompactHeaderProgress('.woodcutting-interface', SKILLS.WOODCUTTING);

    // Update action progress if woodcutting is active
    const action = gameState.getCurrentAction();
    if (!action || action.type !== 'woodcutting') return;

    const activeCard = document.querySelector(`.tree-card[data-tree-id="${action.tree.id}"]`);
    if (activeCard) {
        const cardProgressFill = activeCard.querySelector('.tree-progress-fill');
        if (cardProgressFill) {
            cardProgressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

/**
 * Setup woodcutting skill listeners
 */
export function initWoodcuttingUI() {
    woodcuttingSkill.initializeStartingTool();
    woodcuttingSkill.onChoppingComplete = (tree, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Woodcutting level ${xpResult.newLevel}!`);
        }

        // Only re-render if woodcutting page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.WOODCUTTING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderWoodcuttingUI();
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.woodcuttingUI = {
        toggleChopping,
        upgradeAxe
    };
}

export default {
    renderWoodcuttingUI,
    toggleChopping,
    upgradeAxe,
    updateWoodcuttingUI,
    initWoodcuttingUI
};
